import * as CL from '@design-system/component-library';
import { DialogType } from '../../common/enums.js';
import { useStartFixer } from '../../common/hooks/useStartFixer';
import { elisaMobiiliEoeLinkMsg, getLocale, supportRequestMsg, t } from '../../common/i18n/index.js';
import { openSubscriptionDialog } from '../../common/utils/subscriptionUtils.js';
import { SubscriptionStatusType } from '../../generated/api/models.js';
import { useConfig } from '../../public/site/ConfigProvider';
import type { DialogParams } from '../../common/types/dialog.js';
import type { Subscription } from '../../generated/api/models.js';

export enum SubscriptionDetailsButtonType {
  SUPPORT_REQUEST,
  SUBMIT_SUPPORT_CASE,
  RING_SERVICE,
  BROADBAND_FIXER,
  MOBILE_FIXER,
  MOBILE_CERTIFICATE,
}

interface ButtonData {
  type: SubscriptionDetailsButtonType;
  onClickCallback: () => void;
  icon: string;
  label: string;
  disabled?: boolean;
}

interface ButtonGroupProps {
  buttonTypes: SubscriptionDetailsButtonType[];
  portal?: 'NOE' | 'EOE';
  subscription: Subscription;
  hasPendingActions?: boolean;
  onShowDialog?: (params: DialogParams) => void;
  mdmId?: string;
}

export const ButtonGroup = ({
  buttonTypes,
  portal = 'NOE',
  subscription,
  hasPendingActions,
  onShowDialog,
  mdmId,
}: ButtonGroupProps) => {
  const subscriptionId = subscription.subscriptionId;
  const { ringBaseUrl } = useConfig();
  const startFixer = useStartFixer();
  const MOBILE_CERTIFICATE_URL = 'https://yrityksille.elisa.fi/ohjeet/mobiilivarmenne';

  const buttonData: ButtonData[] = [
    {
      type: SubscriptionDetailsButtonType.SUPPORT_REQUEST,
      onClickCallback: () => {
        if (onShowDialog) {
          openSubscriptionDialog(
            onShowDialog,
            {
              type: DialogType.SUBSCRIPTION_SUPPORT_REQUEST,
              mdmId,
            },
            hasPendingActions
          );
        }
      },
      label: t.ROBH(supportRequestMsg),
      icon: 'maintenance',
    },
    {
      type: SubscriptionDetailsButtonType.SUBMIT_SUPPORT_CASE,
      onClickCallback: () => {
        if (onShowDialog) {
          openSubscriptionDialog(
            onShowDialog,
            {
              subscriptionId,
              type: DialogType.SUBSCRIPTION_SUBMIT_SUPPORT_CASE,
            },
            hasPendingActions
          );
        }
      },
      label: t.ROBH(supportRequestMsg),
      icon: 'maintenance',
    },
    {
      type: SubscriptionDetailsButtonType.RING_SERVICE,
      onClickCallback: () => {
        window.open(`${ringBaseUrl}/autologin`, '_blank');
      },
      label: t.V07Q('Oma Ring'),
      icon: 'settings',
    },
    {
      type: SubscriptionDetailsButtonType.BROADBAND_FIXER,
      onClickCallback: () => startFixer(subscription, getLocale(), portal),
      label: t.OTF5('Connection problems?'),
      disabled: subscription.subscriptionStatus !== SubscriptionStatusType.ACTIVE,
      icon: 'wifi',
    },
    {
      type: SubscriptionDetailsButtonType.MOBILE_FIXER,
      onClickCallback: () => startFixer(subscription, getLocale(), portal),
      label: t.OTF5('Connection problems?'),
      disabled: subscription.subscriptionStatus !== SubscriptionStatusType.ACTIVE,
      icon: 'wifi',
    },
    {
      type: SubscriptionDetailsButtonType.MOBILE_CERTIFICATE,
      onClickCallback: () => {
        window.open(MOBILE_CERTIFICATE_URL, '_blank');
      },
      label: t.LIQB(elisaMobiiliEoeLinkMsg),
      icon: 'external-link',
    },
  ];

  const renderButton = (buttonType: SubscriptionDetailsButtonType) => {
    const buttonDetails: ButtonData = buttonData.find(element => element.type === buttonType) || ({} as ButtonData);
    const iconColor = buttonDetails.disabled ? 'neutral-400--light' : 'brand-blue';
    return (
      <CL.Button
        onClick={buttonDetails.onClickCallback}
        disabled={buttonDetails.disabled}
        size="l"
        color="link"
        key={buttonDetails.type}
      >
        <CL.Icon icon={buttonDetails.icon} color={iconColor} />
        <>{buttonDetails.label}</>
      </CL.Button>
    );
  };

  return (
    <div className="of-subscription-details__actions">{buttonTypes.map(buttonType => renderButton(buttonType))}</div>
  );
};
